/**
 * Language Test Widget
 *
 * Displays language proficiency tests (English / Portuguese) with per-test
 * completion tracking. Opens a modal for each test with instructions and upload.
 *
 * Sub-components:
 *  - LanguageTestCard — animated card with completion state
 *  - LanguageTestModal — rich modal with instructions + upload flow
 */

import { cn } from '@tonnie/foundation';
import { useTranslation } from '@tonnie/locale';
import { useAuth, useRecordStandaloneTest } from '@tonnie/sdk';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useWidgetState } from '@/features/platform/copilot/flow-lifecycle';

import { Loader2 } from '@tonnie/assets';

import {
  assessedLevel,
  combineCefrFromSkills,
  requireCertifiedLanguage,
  type ILanguageTestWidgetProps,
  type PersistedTestCompletion,
} from './language-test.types';
import { LanguageTestCard } from './LanguageTestCard';
import { LanguageTestModal } from './LanguageTestModal';
import { useCheckupReuse } from '../reuse/use-checkup-reuse';
import { CheckupReusePrompt } from '../reuse/CheckupReusePrompt';

/**
 * Persistent slice — shape kept JSON-friendly because the engine ships
 * the snapshot through `widgetStates` (server jsonb). Map<> would
 * serialise to `{}`, dropping every completion silently. Keyed object
 * by test id is the canonical replacement.
 */
/**
 * Index signature is required because `useWidgetState`'s generic
 * extends `Record<string, unknown>` (the server stores opaque jsonb).
 * Tightening the value with the named field still types call sites
 * narrowly via the `completed` accessor.
 */
type PersistedLanguageTestState = {
  /**
   * `{ [testId]: { isCompleted, cefrLevel } }` — defaults to {} on first mount.
   * Read through `assessedLevel`: an entry counts only WITH a level.
   */
  completed: Record<string, PersistedTestCompletion>;
} & Record<string, unknown>;

export function LanguageTestWidget({
  config,
  onComplete,
  onTestComplete,
}: ILanguageTestWidgetProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const certifiedLanguage = requireCertifiedLanguage(config.language);
  // BL-214: a standalone TestIt run banks the CEFR to the reusable store and
  // must talk to the citizen self endpoints with the 'me' sentinel (the Clerk
  // `user.id` is not a uuid and 500s them). Default/absent = 'checkup'.
  const isTestit = config.submissionMode === 'testit';
  const talentId = isTestit ? (config.talentId ?? 'me') : (user?.id ?? '');
  const recordStandalone = useRecordStandaloneTest(talentId || 'me');

  // Persistent slice — survives tab close, refresh, cross-device login.
  // Pre-2026-04-28 (UX widget-state Phase) the widget used a local
  // `useState<Map<string, ITestState>>` which dropped EVERY completion
  // on unmount. Users who finished Speaking and left for any other
  // page (or hit the StrictMode double-mount) would land back on the
  // widget with all four tests un-checked. The persisted slice now
  // keeps the completed map on the server keyed under `language_test`.
  //
  // CRITICAL: scope the persistence channel by `config.language`. The
  // widget renders TWICE in the CheckUp flow (one English chat, one
  // Portuguese) with overlapping test ids (`reading_listening` exists
  // in both). Without the per-language scope, completing English's
  // `reading_listening` would also mark Portuguese's `reading_
  // listening` as done — which is exactly the "PT appeared as Filled
  // when it was not" bug in the 2026-04-28 logs. The
  // `chatIdOverride` arg is the canonical way to namespace the server
  // key (`<override>::<widgetName>`); using `language` instead of the
  // chatId keeps the namespace stable across chat-id renames in the
  // brain config.
  const [persisted, patchPersisted] = useWidgetState<PersistedLanguageTestState>(
    'language_test',
    { completed: {} },
    { chatIdOverride: certifiedLanguage }
  );
  const [selectedTestId, setSelectedTestId] = useState<string | null>(null);

  // BL-214: inside CheckUp, offer to reuse a valid banked language result
  // (english → language.en, portuguese → language.pt). Read via the 'me'
  // sentinel; never in a standalone TestIt run.
  const isCheckup = !isTestit;
  const reuseTestId = config.language === 'portuguese' ? 'language.pt' : 'language.en';
  const { reusable, isLoading: reuseLoading } = useCheckupReuse(reuseTestId, 'me', {
    enabled: isCheckup,
  });
  const [recordNew, setRecordNew] = useState(false);

  const openTestModal = (testId: string) => setSelectedTestId(testId);
  const closeModal = useCallback(() => setSelectedTestId(null), []);

  /**
   * Mount-time auto-advance for already-complete persisted state.
   *
   * If the widget mounts with `requireAllTests=true` AND every required
   * test in `persisted.completed` is already marked `isCompleted`, fire
   * `onComplete()` once after a short delay so the user briefly sees
   * the filled cards (context for why the flow is auto-advancing) and
   * the engine transitions past this chat.
   *
   * Without this, a user who returns to a checkup whose
   * `widgetStates['<lang>::language_test']` is already complete (cross-
   * device resume, page refresh after upload but before chat
   * transition, residue of an earlier finalize that didn't land) is
   * stranded on a widget showing "C1 — Advanced" cards with no
   * advance button — `handleTestComplete` is the only call site that
   * fires `onComplete()` and it only runs from the modal's submit
   * path. Radar 2026-05-08 — root cause of "I see English Test
   * already filled, can't move forward."
   *
   * Mount-only on purpose: the live-completion path (last test
   * finished in this session) is already handled by
   * `handleTestComplete:93-99` calling `onComplete()` directly. Re-
   * running this effect on every `persisted.completed` change would
   * duplicate that path AND fire on the user's first interaction
   * before they intend to advance.
   */
  const autoAdvancedRef = useRef(false);
  useEffect(() => {
    if (autoAdvancedRef.current) return;
    if (!config.requireAllTests) return;
    const allDone = config.tests.every((tc) => assessedLevel(persisted.completed[tc.id]) !== null);
    if (!allDone) return;
    autoAdvancedRef.current = true;
    const timer = setTimeout(() => onComplete(), 800);
    return () => clearTimeout(timer);
    // Mount-only: see docblock above. Live completion goes through
    // `handleTestComplete`, which has its own onComplete() call.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTestComplete = useCallback(
    (testId: string, cefrLevel: string) => {
      const nextCompleted = {
        ...persisted.completed,
        [testId]: { isCompleted: true, cefrLevel },
      };
      patchPersisted({ completed: nextCompleted });

      closeModal();

      const testConfig = config.tests.find((tc) => tc.id === testId);
      if (testConfig && onTestComplete) {
        onTestComplete(testId, testConfig.name);
      }

      // Whole-test completion, checked against the new map (not stale `persisted`).
      const allTestsDone = config.tests.every((tc) => assessedLevel(nextCompleted[tc.id]) !== null);

      // BL-214: a standalone TestIt run banks ONE combined CEFR result per
      // language test — the reusable unit is the whole English/Portuguese test,
      // not each skill (see the @tonnie/heroes test-catalog design note). Banking
      // per-skill made the reading/listening row supersede the speaking row in the
      // store, so the catalog card + modal history showed only half the test. We
      // bank once, when every skill is in, merging speaking + reading/listening
      // into result.cefr. Never fires in CheckUp mode — there CheckUp reads the
      // CEFR from the analyzed vault files, not this store row.
      if (isTestit && config.testitTestId && allTestsDone) {
        const cefr = combineCefrFromSkills(config.tests, nextCompleted);
        if (cefr.overall || cefr.speaking || cefr.reading) {
          recordStandalone.mutate({
            testId: config.testitTestId,
            result: { cefr },
            score: cefr.overall ?? null,
          });
        }
      }

      if (config.requireAllTests && allTestsDone) {
        setTimeout(() => onComplete(), 300);
      }
    },
    [
      config,
      persisted.completed,
      patchPersisted,
      onTestComplete,
      onComplete,
      closeModal,
      isTestit,
      recordStandalone,
    ]
  );

  const getLanguageTitle = () => {
    if (config.language === 'english') return t('checkup.languageTests.englishTests');
    if (config.language === 'portuguese') return t('checkup.languageTests.portugueseTests');
    return t('checkup.languageTests.languageTests');
  };

  // ─── Reuse gate (CheckUp only) ───
  // A valid banked CEFR already lives in the vault where MESH reads it, so
  // "reuse" just completes the step. "Take a new one" falls through to the cards.
  if (isCheckup && !recordNew && (reuseLoading || reusable)) {
    return reuseLoading || !reusable ? (
      <div className="flex items-center justify-center py-16" aria-busy="true">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    ) : (
      <CheckupReusePrompt
        result={reusable}
        onReuse={() => onComplete()}
        onRecordNew={() => setRecordNew(true)}
      />
    );
  }

  const selectedTest = config.tests.find((tc) => tc.id === selectedTestId);

  return (
    <>
      <div className="w-full space-y-4" data-testid="widget-language_test">
        <div className="mb-2">
          <h3 className="text-base font-semibold text-foreground mb-1">{getLanguageTitle()}</h3>
          <p className="text-sm text-muted-foreground">{t('checkup.languageTests.instructions')}</p>
        </div>

        <div
          className={cn(
            'grid gap-4',
            config.tests.length === 1
              ? 'grid-cols-1'
              : config.tests.length === 2
                ? 'grid-cols-2'
                : 'grid-cols-2 lg:grid-cols-3'
          )}
        >
          {config.tests.map((test) => (
            <LanguageTestCard
              key={test.id}
              testConfig={test}
              assessedLevel={assessedLevel(persisted.completed[test.id])}
              onOpen={() => openTestModal(test.id)}
            />
          ))}
        </div>
      </div>

      {selectedTest && (
        <LanguageTestModal
          testConfig={selectedTest}
          talentId={talentId}
          language={certifiedLanguage}
          onClose={closeModal}
          onComplete={(cefrLevel) => handleTestComplete(selectedTest.id, cefrLevel)}
        />
      )}
    </>
  );
}

export default LanguageTestWidget;
